import { useEffect } from "react";
import type { MovieDetail } from "../types";

interface DetailModalProps {
  movie: MovieDetail | null;
  onClose: () => void;
  onNotify: (message: string) => void;
}

export default function DetailModal({ movie, onClose, onNotify }: DetailModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!movie) return null;

  const meta = [movie.year, movie.genre, movie.duration, movie.region].filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-background-50/80 p-0 backdrop-blur-sm md:items-center md:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg overflow-hidden rounded-t-xl border border-background-300/60 bg-background-100 md:rounded-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-background-300/40 p-5">
          <div className="min-w-0">
            {movie.badge ? (
              <span className="mb-2 inline-block rounded-full border border-accent-500/40 bg-accent-500/10 px-2.5 py-0.5 text-[11px] text-accent-300">
                {movie.badge}
              </span>
            ) : null}
            <h3 className="font-display text-xl font-bold text-foreground-950">{movie.title}</h3>
            {movie.en ? <p className="mt-1 text-xs text-foreground-400">{movie.en}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300/60 text-foreground-500 transition-colors hover:text-foreground-900"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-5">
          {meta.length > 0 ? (
            <p className="text-xs text-foreground-500">{meta.join(" · ")}</p>
          ) : null}
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">{movie.summary}</p>

          {movie.tags && movie.tags.length > 0 ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {movie.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-background-300/60 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-500"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : null}

          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => onNotify(`《${movie.title}》即将进入观影（演示）`)}
              className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-fill" />
              开始观看
            </button>
            <button
              type="button"
              onClick={() => onNotify(`已把《${movie.title}》加入深夜片单`)}
              className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 px-4 py-2.5 text-sm text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-300"
            >
              <i className="ri-bookmark-3-line" />
              加入深夜片单
            </button>
          </div>
          <p className="mt-3 text-center text-[11px] text-foreground-400">
            当前为静态展示页，操作仅供预览，不会产生真实播放或下载。
          </p>
        </div>
      </div>
    </div>
  );
}